import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import type { ExtractionCategoryStatus, ExtractionOverallStatus } from "../types"

import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Loader2,
  Minus,
  Sparkles,
  XCircle,
} from "@/components/icons"
import { Progress } from "@/components/ui/progress"
import { cn } from "@/lib/utils"

const CATEGORY_ORDER = [
  "requirements",
  "role_requirements",
  "evaluation_criteria",
  "contract_terms",
  "critical_dates",
  "bid_deliverables",
  "deliverable_documents",
  "timeline",
  "pm_content",
]

type PhaseStatus = "pending" | "running" | "completed" | "failed"

type PhaseRowProps = {
  label: string
  description: string
  status: PhaseStatus
}

function PhaseRow({ label, description, status }: PhaseRowProps) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex size-5 shrink-0 items-center justify-center">
        {status === "completed" && <CheckCircle className="text-success size-4" />}
        {status === "running" && <Loader2 className="text-accent size-4 animate-spin" />}
        {status === "failed" && <XCircle className="text-destructive size-4" />}
        {status === "pending" && <div className="bg-muted-foreground/20 size-2 rounded-full" />}
      </div>
      <div className="min-w-0 flex-1">
        <span
          className={cn(
            "text-sm leading-5 font-medium",
            status === "pending" && "text-muted-foreground/35",
            status === "running" && "text-foreground",
            status === "completed" && "text-muted-foreground/60",
            status === "failed" && "text-destructive"
          )}
        >
          {label}
        </span>
        {status === "running" && (
          <p className="text-muted-foreground mt-0.5 text-xs">{description}</p>
        )}
      </div>
    </div>
  )
}

type CategoryChipProps = {
  name: string
  status: ExtractionCategoryStatus
}

function CategoryChip({ name, status }: CategoryChipProps) {
  let icon: React.ReactNode
  let chipClass: string

  if (status === "completed") {
    icon = <CheckCircle className="text-success size-3" />
    chipClass = "border-success bg-success/10 text-success"
  } else if (status === "extracting") {
    icon = <Loader2 className="text-accent size-3 animate-spin" />
    chipClass = "border-accent bg-accent/[0.06] text-accent-foreground"
  } else if (status === "verifying") {
    icon = <Loader2 className="text-warning size-3 animate-spin" />
    chipClass = "border-warning/30 bg-warning/[0.06] text-warning-foreground"
  } else if (status === "failed") {
    icon = <XCircle className="text-destructive size-3" />
    chipClass = "border-destructive bg-destructive/[0.06] text-destructive"
  } else if (status === "skipped") {
    icon = <Minus className="text-muted-foreground/40 size-3" />
    chipClass = "border-border/40 bg-muted/20 text-muted-foreground/40"
  } else {
    icon = <Clock className="text-muted-foreground/35 size-3" />
    chipClass = "border-border/40 bg-muted/20 text-muted-foreground/50"
  }

  return (
    <div
      className={cn(
        "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors duration-300",
        chipClass
      )}
    >
      {icon}
      {name}
    </div>
  )
}

type ExtractionProgressPanelProps = {
  categoryStatus: Record<string, ExtractionCategoryStatus>
  overallStatus: ExtractionOverallStatus
  hasOverview: boolean
  rateLimitRetry?: {
    attempt: number
    nextRetryAt: string
    phase: string
    sleepSeconds: number
  } | null
}

type CategoryFlags = {
  hasOverview: boolean
  anyExtracting: boolean
  anyVerifying: boolean
  anyCompleted: boolean
  anyFailed: boolean
  allCategoriesDone: boolean
}

function derivePhase1({
  hasOverview,
  anyExtracting,
  anyVerifying,
  anyCompleted,
  anyFailed,
  allCategoriesDone,
}: CategoryFlags): PhaseStatus {
  if (!hasOverview) return "pending"
  if (anyExtracting) return "running"
  if (allCategoriesDone && anyFailed && !anyCompleted) return "failed"
  if (anyVerifying || anyCompleted || allCategoriesDone) return "completed"
  return "pending"
}

function derivePhase2({
  hasOverview,
  anyVerifying,
  anyCompleted,
  anyFailed,
  allCategoriesDone,
}: CategoryFlags): PhaseStatus {
  if (!hasOverview || (!anyVerifying && !anyCompleted && !allCategoriesDone && !anyFailed))
    return "pending"
  if (anyVerifying) return "running"
  if (allCategoriesDone && anyFailed && !anyCompleted) return "failed"
  if (allCategoriesDone) return "completed"
  return "pending"
}

export function ExtractionProgressPanel({
  categoryStatus,
  overallStatus,
  hasOverview,
  rateLimitRetry,
}: ExtractionProgressPanelProps) {
  const { t, i18n } = useTranslation()
  const categoryLabels = useMemo(
    () => ({
      requirements: t("tender.extraction.categories.requirements"),
      evaluation_criteria: t("tender.extraction.categories.evaluation_criteria"),
      contract_terms: t("tender.extraction.categories.contract_terms"),
      critical_dates: t("tender.extraction.categories.critical_dates"),
      role_requirements: t("tender.extraction.categories.role_requirements"),
      bid_deliverables: t("tender.extraction.categories.bid_deliverables"),
      deliverable_documents: t("tender.extraction.categories.deliverable_documents"),
      timeline: t("tender.extraction.categories.timeline"),
      pm_content: t("tender.extraction.categories.pm_content"),
    }),
    [t]
  )

  const statuses = CATEGORY_ORDER.flatMap((cat) =>
    cat in categoryStatus ? [categoryStatus[cat]] : []
  )
  const anyExtracting = statuses.includes("extracting")
  const anyVerifying = statuses.includes("verifying")
  const anyCompleted = statuses.includes("completed")
  const anyFailed = statuses.includes("failed")
  const allCategoriesDone =
    statuses.length > 0 &&
    statuses.every((s) => s === "completed" || s === "failed" || s === "skipped")

  const flags: CategoryFlags = {
    hasOverview,
    anyExtracting,
    anyVerifying,
    anyCompleted,
    anyFailed,
    allCategoriesDone,
  }

  let phase0: PhaseStatus = "running"
  if (overallStatus === "failed" && !hasOverview) phase0 = "failed"
  else if (hasOverview) phase0 = "completed"
  const phase1 = derivePhase1(flags)
  const phase2 = derivePhase2(flags)

  const completedCount = statuses.filter((s) => s === "completed" || s === "skipped").length
  const failedCount = statuses.filter((s) => s === "failed").length
  const totalCount = CATEGORY_ORDER.length
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0

  const isFailed = overallStatus === "failed"
  const isPartial = overallStatus === "partial"
  const effectivePhase1 = isFailed && phase1 === "running" ? "failed" : phase1
  const effectivePhase2 = isFailed && phase2 === "running" ? "failed" : phase2
  let panelBorderClass = "border-accent/60"
  let panelBgClass = "from-accent/[0.04] to-accent/[0.06]"
  let progressTrackClass = "bg-accent/60"
  let progressIndicatorClass = "bg-primary-ink"
  if (isFailed) {
    panelBorderClass = "border-destructive/30"
    panelBgClass = "from-destructive/[0.04] to-destructive/[0.06]"
    progressTrackClass = "bg-destructive/20"
    progressIndicatorClass = "bg-destructive-ink"
  } else if (isPartial) {
    panelBorderClass = "border-warning/30"
    panelBgClass = "from-warning/[0.04] to-warning/[0.06]"
    progressTrackClass = "bg-warning/20"
    progressIndicatorClass = "bg-warning-ink"
  }

  const rateLimitTime =
    rateLimitRetry?.nextRetryAt && !Number.isNaN(Date.parse(rateLimitRetry.nextRetryAt))
      ? new Date(rateLimitRetry.nextRetryAt).toLocaleString(i18n.language, {
          dateStyle: "short",
          timeStyle: "short",
        })
      : null

  return (
    <div
      className={cn(
        "mb-4 overflow-hidden rounded-lg border bg-gradient-to-br p-5",
        panelBorderClass,
        panelBgClass
      )}
    >
      {overallStatus === "processing" && rateLimitRetry && rateLimitTime ? (
        <div className="border-warning/30 bg-warning/10 text-warning-foreground mb-4 rounded-md border px-3 py-2 text-xs">
          {t("tender.rateLimit.waiting", { time: rateLimitTime })}
        </div>
      ) : null}
      {/* Header */}
      <div className="mb-4 flex items-center gap-2">
        {overallStatus === "completed" && (
          <>
            <CheckCircle className="text-success size-4 shrink-0" />
            <p className="text-sm font-semibold">
              {t("tender.extraction.progress.completedTitle")}
            </p>
          </>
        )}
        {overallStatus === "failed" && (
          <>
            <XCircle className="text-destructive size-4 shrink-0" />
            <p className="text-destructive text-sm font-semibold">
              {t("tender.extraction.progress.failedTitle")}
            </p>
          </>
        )}
        {overallStatus === "partial" && (
          <>
            <AlertTriangle className="text-warning size-4 shrink-0" />
            <p className="text-warning-foreground text-sm font-semibold">
              {t("tender.extraction.progress.partialTitle")}
            </p>
          </>
        )}
        {overallStatus !== "completed" &&
          overallStatus !== "partial" &&
          overallStatus !== "failed" && (
            <>
              <Sparkles className="text-accent size-4 shrink-0 animate-pulse" />
              <p className="text-sm font-semibold">
                {t("tender.extraction.progress.runningTitle")}
              </p>
            </>
          )}
      </div>

      {/* Phase steps */}
      <div className="mb-4 space-y-3 pl-0.5">
        <PhaseRow
          description={t("tender.extraction.progress.phase0Description")}
          label={t("tender.extraction.progress.phase0Label")}
          status={phase0}
        />
        <PhaseRow
          description={t("tender.extraction.progress.phase1Description")}
          label={t("tender.extraction.progress.phase1Label")}
          status={effectivePhase1}
        />
        <PhaseRow
          description={t("tender.extraction.progress.phase2Description")}
          label={t("tender.extraction.progress.phase2Label")}
          status={effectivePhase2}
        />
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-muted-foreground text-xs">
            {t("tender.extraction.progress.categoriesDone")}
          </span>
          <span className="text-xs font-semibold tabular-nums">
            {completedCount} / {totalCount}
            {failedCount > 0 && (
              <span className="text-destructive ml-1">
                ({t("tender.extraction.progress.failedChip", { count: failedCount })})
              </span>
            )}
          </span>
        </div>
        <Progress
          className={cn("h-1.5", progressTrackClass)}
          indicatorClassName={cn("rounded-full duration-700 ease-out", progressIndicatorClass)}
          value={progressPercent}
        />
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-2">
        {CATEGORY_ORDER.map((cat) => {
          const rawStatus = categoryStatus[cat] ?? "pending"
          const effectiveStatus =
            isFailed && (rawStatus === "extracting" || rawStatus === "verifying")
              ? "failed"
              : rawStatus
          return (
            <CategoryChip
              key={cat}
              name={categoryLabels[cat as keyof typeof categoryLabels]}
              status={effectiveStatus}
            />
          )
        })}
      </div>
    </div>
  )
}
